import React, { useContext, useEffect, useMemo, useState } from 'react';
import topApi from '../../components/api/ToPApi';
import Message from '../../components/ui/Message';
import { AuthContext } from '../../context/auth';
import { errorMessage, isPlatformUser } from '../../utils/access';
import { featureIcon } from './featureUi';
import { BankLicensePage } from './BankLicensePage';
import { PAYFAC_SIGNUP_ENABLED } from '../../utils/appConfig';

// Funcionalidad que el banco ofrece a sus comercios (solo las que la plataforma le asoció)
interface BankFeature {
    id: number;
    code: string;
    name: string;
    description: string | null;
    base: boolean;
    active: boolean;                // el banco la ofrece
    monthlyPrice: number | null;    // precio vigente al comercio
    currency: string | null;
    perUser?: boolean;              // se cobra por usuario (retail, inventario)
}

// Suscripción de los comercios del registro autogestionado: tarjeta obligatoria y periodo de prueba
interface SubscriptionConfig { cardRequired: boolean; trialEnabled: boolean; trialDays: number; }
interface SubscriptionDraft { cardRequired: boolean; trialEnabled: boolean; trialDays: string; }

const toSubscriptionDraft = (c: SubscriptionConfig): SubscriptionDraft =>
    ({ cardRequired: c.cardRequired, trialEnabled: c.trialEnabled, trialDays: c.trialEnabled ? String(c.trialDays) : '' });

/**
 * Registro autogestionado: si se pide tarjeta para cobrar la suscripción cada mes y si hay periodo de prueba (y cuántos
 * días). Con cobro: si falla se reintenta una vez al día por 3 días y luego la cuenta se suspende hasta que el comercio
 * registre otra tarjeta.
 */
const SubscriptionSettings = () => {
    const [config, setConfig] = useState<SubscriptionConfig | null>(null);
    const [draft, setDraft] = useState<SubscriptionDraft>({ cardRequired: false, trialEnabled: false, trialDays: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        topApi.get<SubscriptionConfig>('/merchant-subscription/config')
            .then(resp => { setConfig(resp.data); setDraft(toSubscriptionDraft(resp.data)); })
            .catch(e => setError(errorMessage(e)));
    }, []);

    const days = Number(draft.trialDays);
    const daysInvalid = draft.trialEnabled && (!/^\d{1,3}$/.test(draft.trialDays.trim()) || days < 1 || days > 365);
    const changed = !!config && (draft.cardRequired !== config.cardRequired || draft.trialEnabled !== config.trialEnabled
        || (draft.trialEnabled && days !== config.trialDays));

    const save = async () => {
        if (!changed || daysInvalid) return;
        setSaving(true);
        try {
            const resp = await topApi.put<SubscriptionConfig>('/merchant-subscription/config', {
                cardRequired: draft.cardRequired, trialEnabled: draft.trialEnabled, trialDays: draft.trialEnabled ? days : 0,
            });
            setConfig(resp.data);
            setDraft(toSubscriptionDraft(resp.data));
            setSuccess('Configuración guardada. Aplica a los comercios que se registren desde ahora.');
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    if (!config) return error ? <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" /> : null;

    return (
        <div className="card border-0 shadow-sm mt-4">
            <div className="card-body">
                <h5 className="mb-1">Suscripción de los comercios</h5>
                <p className="text-muted small mb-3">
                    Para los comercios que se registran solos ("Cree su comercio"). El monto mensual es el de las funcionalidades que eligen;
                    las que se cobran por usuario se multiplican por la cantidad de usuarios.
                </p>

                <div className="form-check form-switch mb-2">
                    <input id="sub-card" className="form-check-input" type="checkbox" role="switch" checked={draft.cardRequired} disabled={saving}
                        onChange={e => setDraft(d => ({ ...d, cardRequired: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="sub-card">Pedir tarjeta en el registro y cobrar la suscripción cada mes</label>
                </div>
                <div className="small text-muted mb-3 ms-5">
                    Si un cobro es rechazado se reintenta una vez al día por 3 días; si no se logra, la cuenta se suspende hasta que el comercio registre otra tarjeta al iniciar sesión.
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="form-check form-switch mb-0">
                        <input id="sub-trial" className="form-check-input" type="checkbox" role="switch" checked={draft.trialEnabled}
                            disabled={saving || !draft.cardRequired}
                            onChange={e => setDraft(d => ({ ...d, trialEnabled: e.target.checked, trialDays: e.target.checked && !d.trialDays ? '30' : d.trialDays }))} />
                        <label className="form-check-label" htmlFor="sub-trial">Periodo de prueba</label>
                    </div>
                    {draft.trialEnabled && draft.cardRequired && (
                        <div className="input-group input-group-sm has-validation" style={{ width: 160 }}>
                            <input className={`form-control text-end ${daysInvalid ? 'is-invalid' : ''}`} inputMode="numeric" aria-label="Días de prueba"
                                value={draft.trialDays} disabled={saving} onChange={e => setDraft(d => ({ ...d, trialDays: e.target.value }))} />
                            <span className="input-group-text">días</span>
                            {daysInvalid && <div className="invalid-feedback">Entre 1 y 365 días</div>}
                        </div>
                    )}
                </div>
                {draft.trialEnabled && draft.cardRequired && !daysInvalid && (
                    <div className="small text-muted mt-2">El primer cobro se hace {days} {days === 1 ? 'día' : 'días'} después del registro.</div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-3">
                    <button type="button" className="btn btn-outline-secondary" disabled={!changed || saving} onClick={() => setDraft(toSubscriptionDraft(config))}>
                        Deshacer
                    </button>
                    <button type="button" className="btn btn-primary" disabled={!changed || daysInvalid || saving} onClick={save}>
                        {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check2 me-1" />}
                        Aplicar suscripción
                    </button>
                </div>
            </div>

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Cambios guardados" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};

// Cambio de precios guardado que aplica a todos los comercios desde el 1 de `period`
interface ScheduledTariff { period: string; currency: string; prices: Record<string, number>; }

interface OfferResponse {
    bank: { id: number; name: string; countryId: number };
    features: BankFeature[];
    currency: string;               // moneda de toda la oferta del banco
    currencies: string[];           // monedas en que el banco puede poner precio
    blocks: { invoiceBlockSize: number; readBlockSize: number };
    scheduled: ScheduledTariff | null;
    changesFrom: string;            // 'YYYY-MM' desde el que rige un cambio guardado ahora
    immediate: boolean;             // el banco aún no tiene comercios: los precios aplican de inmediato
}

interface Draft { active: boolean; price: string; }

const FEATURE_ELECTRONIC_INVOICE = 'electronic-invoice';
const FEATURE_ACCOUNTING = 'accounting';

const CURRENCY_SYMBOL: Record<string, string> = { CRC: '₡', USD: '$' };

const amount = (value: number | null | undefined) => (value === null || value === undefined ? '' : String(value));

// Lo que se edita: el cambio programado si hay; si no, los precios vigentes
const toDraft = (data: OfferResponse): Record<string, Draft> =>
    Object.fromEntries(data.features.map(f => [f.code, {
        active: !!f.active,
        price: amount(data.scheduled?.prices[f.code] ?? f.monthlyPrice),
    }]));
const currencyOf = (data: OfferResponse) => data.scheduled?.currency ?? data.currency;

const validAmount = (value: string) => /^\d{1,8}(\.\d{1,2})?$/.test(value.trim());
const fmt = (n: number) => n.toLocaleString('es-CR');

// '2026-12' → '1 de diciembre de 2026'
const firstDayOf = (period: string) => {
    const [y, m] = period.split('-').map(Number);
    return y && m ? new Date(y, m - 1, 1).toLocaleDateString('es-CR', { day: 'numeric', month: 'long', year: 'numeric' }) : period;
};

const money = (value: number | null | undefined, currency: string) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('es-CR', { style: 'currency', currency, maximumFractionDigits: currency === 'CRC' ? 0 : 2 }).format(value);
};

// Facturación electrónica y contabilidad se cobran por bloque de uso: llevan * (ver nota al pie)
const BLOCK_FEATURES = [FEATURE_ELECTRONIC_INVOICE, FEATURE_ACCOUNTING];

/**
 * Funcionalidades: la plataforma configura el licenciamiento de cada banco (BankLicensePage); el banco, su oferta.
 */
export const BankFeaturesPage = () => {
    const { user } = useContext(AuthContext);
    return isPlatformUser(user) ? <BankLicensePage /> : <BankOfferPage />;
};

/**
 * Oferta del banco: dentro de lo que la plataforma le asoció, decide qué ofrece a sus comercios y a qué precio, en
 * una sola moneda. Facturación electrónica y contabilidad se cobran al comercio por bloque de uso del mes (los
 * tamaños los define la plataforma). Ofrecer o no aplica de inmediato; los precios, a todos los comercios desde el
 * 1 del mes siguiente (de inmediato si el banco aún no tiene comercios).
 */
const BankOfferPage = () => {
    const [data, setData] = useState<OfferResponse | null>(null);
    const [draft, setDraft] = useState<Record<string, Draft>>({});
    const [currency, setCurrency] = useState('USD');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        setLoading(true);
        topApi.get<OfferResponse>('/features/offer')
            .then(resp => { setData(resp.data); setDraft(toDraft(resp.data)); setCurrency(currencyOf(resp.data)); })
            .catch(e => setError(errorMessage(e)))
            .finally(() => setLoading(false));
    }, []);

    const features = useMemo(() => data?.features ?? [], [data]);
    const currencies = data?.currencies ?? ['USD'];
    const blocks = data?.blocks ?? { invoiceBlockSize: 500, readBlockSize: 200 };

    const changed = useMemo(() => {
        if (!data) return [];
        const baseline = toDraft(data);
        return features.filter(f => {
            const d = draft[f.code];
            return !!d && (d.active !== baseline[f.code].active || d.price.trim() !== baseline[f.code].price);
        });
    }, [data, features, draft]);
    const currencyChanged = !!data && currency !== currencyOf(data);
    const changes = changed.length + (currencyChanged ? 1 : 0);

    const invalidOf = (f: BankFeature) => !!draft[f.code] && !validAmount(draft[f.code].price);
    const invalid = features.some(invalidOf);

    const set = (code: string, patch: Partial<Draft>) => setDraft(prev => ({ ...prev, [code]: { ...prev[code], ...patch } }));

    const save = async () => {
        if (!data || invalid || !changes) return;
        setSaving(true);
        try {
            // Siempre la oferta completa: los precios forman un solo cambio, en una sola moneda
            const resp = await topApi.put<OfferResponse>('/features/offer', {
                currency,
                features: features.map(f => {
                    const d = draft[f.code];
                    return { code: f.code, active: d.active, monthlyPrice: Number(d.price) };
                }),
            });
            setData(resp.data);
            setDraft(toDraft(resp.data));
            setCurrency(currencyOf(resp.data));
            setSuccess(data.immediate
                ? 'Cambios guardados. Los precios ya aplican a los comercios.'
                : `Cambios guardados. Los precios aplican a todos los comercios desde el ${firstDayOf(data.changesFrom)}.`);
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setSaving(false);
        }
    };

    // Qué cubre el precio: un bloque de uso del mes o el mes completo
    const priceUnit = (code: string, perUser?: boolean) => {
        if (perUser) return 'por usuario al mes';
        if (code === FEATURE_ELECTRONIC_INVOICE) return `por comercio al mes · incluye ${fmt(blocks.invoiceBlockSize)} documentos emitidos`;
        if (code === FEATURE_ACCOUNTING) return `por comercio al mes · incluye ${fmt(blocks.readBlockSize)} facturas leídas`;
        return 'por comercio al mes';
    };

    const priceInput = (f: BankFeature) => {
        const d = draft[f.code];
        const bad = invalidOf(f);
        return (
            <div className="input-group input-group-sm has-validation" style={{ minWidth: 200 }}>
                <span className="input-group-text">{CURRENCY_SYMBOL[currency] ?? currency}</span>
                <input className={`form-control text-end ${bad ? 'is-invalid' : ''}`} inputMode="decimal"
                    aria-label={`Precio al comercio de ${f.name}`} value={d?.price ?? ''} disabled={saving}
                    onChange={e => set(f.code, { price: e.target.value })} />
                {bad && <div className="invalid-feedback">Monto con hasta 2 decimales</div>}
            </div>
        );
    };

    const hasInvoice = features.some(f => f.code === FEATURE_ELECTRONIC_INVOICE);
    const hasAccounting = features.some(f => f.code === FEATURE_ACCOUNTING);

    return (
        <div className="mt-5 container-fluid px-4" style={{ flex: 1, marginTop: 10 }}>
            <div className="mb-4">
                <h3 className="mb-1">Funcionalidades</h3>
                <p className="text-muted mb-0">Active las funcionalidades que ofrece su banco a los comercios y defina su precio mensual.</p>
            </div>

            {loading ? (
                <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
            ) : data && (
                <div className="card border-0 shadow-sm">
                    <div className="card-body">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                            <div className="d-flex align-items-center gap-2">
                                <label className="small text-muted mb-0" htmlFor="offer-currency">Moneda de los precios</label>
                                {currencies.length > 1 ? (
                                    <select id="offer-currency" className="form-select form-select-sm" style={{ width: 160 }} value={currency}
                                        disabled={saving} onChange={e => setCurrency(e.target.value)}>
                                        {currencies.map(c => <option key={c} value={c}>{c === 'CRC' ? 'Colones (CRC)' : 'Dólares (USD)'}</option>)}
                                    </select>
                                ) : (
                                    <span id="offer-currency" className="fw-medium">{currency === 'CRC' ? 'Colones (CRC)' : 'Dólares (USD)'}</span>
                                )}
                            </div>
                            {data.scheduled && (
                                <span className="badge rounded-pill bg-info-subtle text-info-emphasis fw-normal">
                                    <i className="bi bi-calendar-event me-1" />Cambio de precios programado desde el {firstDayOf(data.scheduled.period)}
                                </span>
                            )}
                        </div>
                        {features.length === 0 && <p className="text-muted">Su banco aún no tiene servicios asociados.</p>}
                        <div className="table-responsive">
                            <table className="table align-middle">
                                <thead>
                                    <tr className="text-muted small text-uppercase">
                                        <th>Funcionalidad</th>
                                        <th style={{ width: 110 }}>Ofrecer</th>
                                        <th style={{ width: 280 }}>Precio al comercio</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {features.map(f => {
                                        const d = draft[f.code];
                                        const id = `offer-${f.code}`;
                                        return (
                                            <tr key={f.code} className={d?.active ? '' : 'text-muted'}>
                                                <td>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <span className="text-primary fs-5"><i className={`bi ${featureIcon(f.code)}`} /></span>
                                                        <div>
                                                            <div className="fw-medium">
                                                                {f.name}{BLOCK_FEATURES.includes(f.code) && <span className="text-primary">*</span>}
                                                                {f.base && <span className="badge bg-secondary-subtle text-secondary-emphasis ms-2">Siempre incluida</span>}
                                                            </div>
                                                            {f.description && <div className="small text-muted">{f.description}</div>}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="form-check form-switch mb-0">
                                                        <input id={id} className="form-check-input" type="checkbox" role="switch" checked={!!d?.active} disabled={f.base || saving}
                                                            onChange={e => set(f.code, { active: e.target.checked })} />
                                                        <label className="form-check-label small" htmlFor={id}>{d?.active ? 'Sí' : 'No'}</label>
                                                    </div>
                                                </td>
                                                <td>
                                                    {priceInput(f)}
                                                    <div className="small text-muted mt-1">{priceUnit(f.code, f.perUser)}</div>
                                                    {data.scheduled && f.monthlyPrice !== null && amount(f.monthlyPrice) !== d?.price.trim() && (
                                                        <div className="small text-muted">Vigente este mes: {money(f.monthlyPrice, f.currency ?? data.currency)}</div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {(hasInvoice || hasAccounting) && (
                            <p className="small text-muted">
                                *Precios por comercio activo al mes.
                                {hasInvoice && <> Facturación electrónica incluye hasta {fmt(blocks.invoiceBlockSize)} documentos emitidos al mes; cada bloque adicional de {fmt(blocks.invoiceBlockSize)} se cobra al mismo precio.</>}
                                {hasAccounting && <> Contabilidad incluye hasta {fmt(blocks.readBlockSize)} facturas leídas al mes; cada bloque adicional de {fmt(blocks.readBlockSize)} se cobra al mismo precio.</>}
                            </p>
                        )}

                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <span className="small text-muted">
                                {data.immediate
                                    ? 'Su banco aún no tiene comercios: los precios aplican de inmediato.'
                                    : `Los precios aplican a todos los comercios desde el ${firstDayOf(data.changesFrom)}. Ofrecer o no una funcionalidad aplica de inmediato.`}
                            </span>
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-outline-secondary" disabled={!changes || saving}
                                    onClick={() => { setDraft(toDraft(data)); setCurrency(currencyOf(data)); }}>
                                    Descartar
                                </button>
                                <button type="button" className="btn btn-primary" disabled={!changes || invalid || saving} onClick={save}>
                                    {saving ? <span className="spinner-border spinner-border-sm me-2" /> : <i className="bi bi-check2 me-1" />}
                                    Guardar {changes > 0 ? `(${changes})` : ''}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Suscripción de los comercios: solo en la instancia Swiftmart (Swiftpay) */}
            {PAYFAC_SIGNUP_ENABLED && !loading && data && <SubscriptionSettings />}

            {error && <Message type="error" message={error} closeDialog={() => setError('')} handleOk={() => setError('')} backgroundColor="rgba(0,0,0,0.4)" />}
            {success && <Message type="success" title="Cambios guardados" message={success} closeDialog={() => setSuccess('')} handleOk={() => setSuccess('')} backgroundColor="rgba(0,0,0,0.4)" />}
        </div>
    );
};
